import { Separator } from "@/components/ui/separator";
import { Outlet, useParams } from "react-router-dom";
import { FetchTenderByIdQuery, MarginDecisionGroup, Tender, TenderPermissionEnum, TenderState } from "@/graphql/__generated__/graphql";
import { formatDateLocale, getUserFriendlyTenderState, handleTenderStateColour } from "@/utils/helpers";
import { TenderSideNavigationMenu } from "@/pages/EditTender/Components";
import { useFetchTenderByIdQueryHook } from "@/hooks/queries";
import { useTenderStateData } from "@/hooks/cache";
import TenderOverviewDetailsComponent, { TenderOverviewDetails } from "@/pages/EditTender/Components/TenderOverviewDetailsComponent";
import { useTranslation } from "react-i18next";
import { useBreakpoint } from "@/hooks";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState } from "react";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import useUserStore from "@/store/shield";
import { DocumentProvider } from "@/context/DocumentProvider";
import { Badge } from "@/components/ui/badge";
import { CircleLoader } from "@/components/shared/loaders";
import { cn } from "@/lib/utils";
import { ArchiveIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import FloatingChat from "@/components/shared/FloatingChat";
import { useIntl } from "react-intl";
import CalculationOverviewWrapperDialog from "@/pages/EditTender/Components/CalculationOverviewWrapperDialog";

export type EditTenderContext = {
  tenderByIdData: FetchTenderByIdQuery;
  tenderByIdLoading: boolean;
  tenderByIdRefetch: () => void;
  isNewService: boolean;
  isTenderOwner: boolean;
  canAddService: boolean;
};

/**
 * EditTenderLayout component renders the layout used for editing a tender.
 *
 * This component fetches tender data, handles responsive navigation menus,
 * and conditionally displays a floating card and document chat provider.
 *
 * @returns {JSX.Element} The rendered tender editing layout.
 */
const EditTenderLayout = () => {

  // Hooks
  const { tenderID } = useParams();
  const { t } = useTranslation(["translation", "common", "components"]);
  const isMdBreakpoint = useBreakpoint("md");
  const userId: number | undefined = useUserStore((state) => state.userId);
  const intl = useIntl();

  // States
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Query hook to fetch tender by ID
  const { tenderByIdData, tenderByIdLoading, tenderByIdRefetch } = useFetchTenderByIdQueryHook(Number(tenderID));

  // Read the tender state from cache (must be called before any early returns)
  const tenderState: TenderState | null = useTenderStateData(Number(tenderID));

  // Render a loading state while fetching tender data
  if (tenderByIdLoading && !tenderByIdData) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <CircleLoader
          visible={true}
          height={80}
          width={80}
          strokeWidth={8}
          ariaLabel="oval-loading"
        />
      </div>
    );
  }

  // Retrieve tender data and define related constants
  const tenderData = tenderByIdData?.fetchTenderById;
  const isNewService = location.pathname.includes("/mobile/new") || location.pathname.includes("/permanent/new");
  const bidDecisionGroups = tenderData?.bidDecisionGroups ?? [];
  const marginDecisionGroups = tenderData?.marginDecisionGroups ?? [];
  const isTenderOwner = tenderData?.description.ownerId === userId;

  // Determine conditional UI elements based on the current pathname
  const isDocumentChat = location.pathname.includes("/documentchat");
  const isCalculationOverview = location.pathname.includes("/calculation-overview");

  // Determine if the user has permission to add a service
  const canAddService = tenderData?.permissions.includes(TenderPermissionEnum.AddService);

  // Render an error state if no tender ID is provided
  if (!tenderID) {
    return (
      <div className="flex items-center w-full h-full justify-center text-center">
        No tender ID provided
      </div>
    );
  }

  // Prepare overview details to pass to the TenderOverviewDetailsComponent
  const tenderOverviewDetails: TenderOverviewDetails = {
    margin: tenderData?.margin ?? 0,
    estimateMargin: tenderData?.estimate.margin ?? 0,
    revenue: tenderData?.revenue ?? 0,
    totalCost: tenderData?.totalCost ?? 0,
    minutes: tenderData?.minutes ?? 0,
    surcharge: tenderData?.surcharge ?? 0,
  };


  return (
    <div className="flex flex-col h-dvh">
      {/* Global Form Header */}
      {
        !isDocumentChat && (
          <div className="flex-none flex flex-col gap-2 pt-2 bg-white z-40">
            <div className="flex grow gap-2 flex-row items-center px-4 pt-1 justify-between">
              <div className="flex gap-2 items-center">
                {/* Header Text */}
                {tenderData?.archived &&
                  <Tooltip>
                    <TooltipTrigger>
                      <Badge variant="outline" className="text-white border-0 state-badge text-[10px] bg-gray-500">
                        <ArchiveIcon className="h-4 w-4" />
                        {t("components:tenderoverviewheader.archived.title")}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs max-w-[250px] ml-4">
                      <p className="mb-2">{t("components:tenderoverviewheader.archived.description")}</p>
                      <p>{t("components:tenderoverviewheader.archived.archivedreason")}: {tenderData?.archiveReason}</p>
                    </TooltipContent>
                  </Tooltip>
                }
                <Badge variant="outline" className={cn("text-white border-0 state-badge text-[10px] mr-2", handleTenderStateColour(tenderData?.state as TenderState, true, true, false))}>
                  {getUserFriendlyTenderState(t, tenderData?.state as TenderState)}
                </Badge>
              </div>
              <div className="grow flex items-center gap-4 justify-between overflow-x-auto whitespace-nowrap">
                <TenderOverviewDetailsComponent overviewDetails={tenderOverviewDetails} />
                <div className="flex shrink-0 gap-2 text-xs">
                  <div className="text-gray-400">{t("edittender.header.contractperiod")}</div>
                  <div>{formatDateLocale(intl, tenderData?.contract.startDate ?? new Date())} - {formatDateLocale(intl, tenderData?.contract.endDate ?? new Date())}</div>
                </div>
              </div>

              {!isCalculationOverview && <CalculationOverviewWrapperDialog tenderData={tenderData as Tender} />}
            </div>
            <Separator />
          </div>
        )
      }

      <div className="flex flex-1 min-h-0">
        {/* Navigation Menu */}
        <div className="flex-none z-50">
          {
            isMdBreakpoint ? (
              <TenderSideNavigationMenu
                tenderName={tenderData?.description.name ?? ""}
                bidDecisionGroups={bidDecisionGroups}
                marginDecisionGroups={marginDecisionGroups as MarginDecisionGroup[]}
                tenderState={tenderState}
                isDocumentChat={isDocumentChat}
                tenderID={tenderID}
                chatMessages={tenderData?.totalMessages ?? 0}
              />
            ) : (
              <div className="py-3 pl-4 pr-2">
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                  <SheetTrigger data-cy="mobile-menu-trigger">
                    <HamburgerMenuIcon className="icon-sm" />
                  </SheetTrigger>
                  <SheetContent className="w-[300px] px-0" side="left">
                    <SheetHeader>
                      <SheetTitle className="sr-only">WISEBID</SheetTitle>
                    </SheetHeader>
                    <TenderSideNavigationMenu
                      tenderName={tenderData?.description.name ?? ""}
                      inSheet={true}
                      setIsOpen={setIsOpen}
                      bidDecisionGroups={bidDecisionGroups}
                      marginDecisionGroups={marginDecisionGroups as MarginDecisionGroup[]}
                      tenderState={tenderState}
                      isDocumentChat={isDocumentChat}
                      tenderID={tenderID}
                      chatMessages={tenderData?.totalMessages ?? 0}
                    />
                  </SheetContent>
                </Sheet>
              </div>
            )
          }
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto">
          {/* Used for /documentchat */}
          <DocumentProvider>
            <Outlet context={{
              tenderByIdData,
              tenderByIdLoading,
              tenderByIdRefetch,
              isNewService,
              isTenderOwner,
              canAddService,
            }} />
          </DocumentProvider>

          {!isDocumentChat && <FloatingChat tenderByIdDataContext={tenderByIdData} />}
        </div>
      </div>
    </div>
  );
};

export default EditTenderLayout;
